<template>
  <div>
    <div class="mb-4">
      <!-- Loading skeleton -->
      <div v-if="loading" class="d-flex flex-column gap-3">
        <div v-for="n in 2" :key="n" class="skeleton-card">
          <div class="skeleton-card__header">
            <div class="skeleton-line skeleton-line--sm" style="width:60%"></div>
            <div class="skeleton-line skeleton-line--xs" style="width:30%"></div>
          </div>
          <div class="skeleton-card__body">
            <div class="skeleton-line" style="width:45%"></div>
            <div class="skeleton-line skeleton-line--lg" style="width:55%"></div>
          </div>
          <div class="skeleton-card__footer">
            <div class="skeleton-btn"></div>
          </div>
        </div>
      </div>

      <!-- Error state -->
      <div v-else-if="error" class="section-error">
        <i class="fas fa-circle-exclamation section-error__icon"></i>
        <p class="section-error__text">{{ error }}</p>
        <button class="btn btn-outline-danger btn-sm" @click="$emit('retry')">
          <i class="fas fa-rotate-right me-1"></i>Tentar novamente
        </button>
      </div>

      <div v-else-if="eventBalanceSummary.length > 0" class="d-flex flex-column gap-3">
        <EventBalanceCard
          v-for="item in eventBalanceSummary"
          :key="item.event_id"
          :item="item"
          :requesting="requestingWithdrawalForEvent === item.event_id"
          :requesting-early="requestingEarlyPixForEvent === item.event_id"
          :fixed-fee="fixedFee"
          @request-withdrawal="onRequestWithdrawal"
          @early-pix-withdrawal="onEarlyPixWithdrawal"
        />
      </div>

      <!-- Empty state -->
      <div v-else class="sacar-empty-state">
        <div class="sacar-empty-state__icon">
          <i class="fas fa-gift"></i>
        </div>
        <template v-if="totalEmTransferencia > 0">
          <p class="sacar-empty-state__title">Transferência em andamento</p>
          <p class="sacar-empty-state__desc">
            {{ formatCurrency(totalEmTransferencia) }} está sendo enviado para sua conta Pix. Você receberá uma confirmação assim que o processo for concluído.
          </p>
        </template>
        <template v-else-if="totalBloqueado > 0">
          <p class="sacar-empty-state__title">Aguardando liberação</p>
          <p class="sacar-empty-state__desc">
            Seu saldo está aguardando o prazo de liberação. Em breve ficará disponível para transferência.
          </p>
        </template>
        <template v-else>
          <p class="sacar-empty-state__title">Sem saldo por enquanto</p>
          <p class="sacar-empty-state__desc">
            Quando seus convidados confirmarem os pagamentos, o saldo aparecerá aqui — pronto para ser transferido para sua conta.
          </p>
        </template>
      </div>
    </div>

    <!-- Configurações de Recebimento: colapsável e discreta -->
    <div class="payout-config mt-4">
      <button class="payout-config__toggle" @click="payoutConfigExpanded = !payoutConfigExpanded">
        <span class="payout-config__toggle-left">
          <i class="fas fa-sliders me-2" style="font-size:0.8rem;color:#9ca3af;"></i>
          <span class="payout-config__toggle-label">Configurações de recebimento</span>
        </span>
        <i :class="['fas', payoutConfigExpanded ? 'fa-chevron-up' : 'fa-chevron-down']" style="font-size:0.72rem;color:#9ca3af;"></i>
      </button>

      <div v-if="payoutConfigExpanded" class="setup-cards-grid mt-3">

        <!-- Card: Conta Bancária (para cartão) -->
        <div class="setup-card">
          <div class="setup-card__header">
            <div class="setup-card__icon-wrap setup-card__icon-wrap--bank">
              <i class="fas fa-university"></i>
            </div>
            <div class="setup-card__titles">
              <span class="setup-card__title">Conta Bancária</span>
              <span class="setup-card__subtitle">Para repasses via cartão</span>
            </div>
            <router-link to="/my-account/bank" class="setup-card__edit" title="Editar dados bancários">
              <i class="fas fa-pen-to-square"></i>
            </router-link>
          </div>
          <div v-if="account" class="setup-card__body">
            <div class="setup-card__summary-row" @click="bankExpanded = !bankExpanded">
              <span :class="['setup-card__badge', account.status === 'verified' ? 'setup-card__badge--ok' : account.status === 'pending' ? 'setup-card__badge--pending' : 'setup-card__badge--warn']">
                <i :class="['fas me-1', account.status === 'verified' ? 'fa-circle-check' : account.status === 'pending' ? 'fa-clock' : 'fa-triangle-exclamation']"></i>
                {{ account.status === 'verified' ? 'Verificado' : account.status === 'pending' ? 'Em análise' : 'Ação necessária' }}
              </span>
              <span class="setup-card__toggle">
                {{ bankExpanded ? 'Ocultar' : 'Ver dados' }}
                <i :class="['fas ms-1', bankExpanded ? 'fa-chevron-up' : 'fa-chevron-down']"></i>
              </span>
            </div>
            <div v-if="bankExpanded" class="setup-card__fields">
              <div class="setup-card__field-row"><span class="setup-card__field-label">Banco</span><span class="setup-card__field-value">{{ account.bank || '—' }}</span></div>
              <div class="setup-card__field-row"><span class="setup-card__field-label">Agência</span><span class="setup-card__field-value">{{ account.agency || '—' }}</span></div>
              <div class="setup-card__field-row"><span class="setup-card__field-label">Conta</span><span class="setup-card__field-value">{{ account.account || '—' }}</span></div>
              <div class="setup-card__field-row setup-card__field-row--last"><span class="setup-card__field-label">Titular</span><span class="setup-card__field-value">{{ account.holder || '—' }}</span></div>
            </div>
          </div>
          <div v-else class="setup-card__empty">
            <p class="setup-card__empty-text">Nenhuma conta cadastrada.</p>
            <router-link to="/my-account/bank" class="setup-card__cta">Cadastrar agora</router-link>
          </div>
        </div>

        <!-- Card: Chave Pix -->
        <div class="setup-card">
          <div class="setup-card__header">
            <div class="setup-card__icon-wrap setup-card__icon-wrap--pix">
              <i class="fas fa-qrcode"></i>
            </div>
            <div class="setup-card__titles">
              <span class="setup-card__title">Chave Pix</span>
              <span class="setup-card__subtitle">Para saques via Pix</span>
            </div>
            <router-link to="/my-account/bank?tab=pix" class="setup-card__edit" :title="pixAccount ? 'Editar chave Pix' : 'Cadastrar chave Pix'">
              <i class="fas fa-pen-to-square"></i>
            </router-link>
          </div>
          <div v-if="pixAccount" class="setup-card__body">
            <div class="setup-card__summary-row" @click="pixExpanded = !pixExpanded">
              <span class="setup-card__badge setup-card__badge--ok">
                <i class="fas fa-circle-check me-1"></i>Cadastrada
              </span>
              <span class="setup-card__toggle">
                {{ pixExpanded ? 'Ocultar' : 'Ver dados' }}
                <i :class="['fas ms-1', pixExpanded ? 'fa-chevron-up' : 'fa-chevron-down']"></i>
              </span>
            </div>
            <div v-if="pixExpanded" class="setup-card__fields">
              <div class="setup-card__field-row"><span class="setup-card__field-label">Tipo</span><span class="setup-card__field-value">{{ pixKeyTypeLabel(pixAccount.agency) }}</span></div>
              <div class="setup-card__field-row"><span class="setup-card__field-label">Chave</span><span class="setup-card__field-value">{{ maskedPixAccountKey }}</span></div>
              <div class="setup-card__field-row setup-card__field-row--last"><span class="setup-card__field-label">Titular</span><span class="setup-card__field-value">{{ pixAccount.holder || '—' }}</span></div>
            </div>
          </div>
          <div v-else class="setup-card__empty">
            <p class="setup-card__empty-text">Nenhuma chave Pix cadastrada.</p>
            <router-link to="/my-account/bank?tab=pix" class="setup-card__cta">Cadastrar agora</router-link>
          </div>
        </div>

      </div>
    </div>
    <div class="pb-6"></div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { formatBRL as formatCurrency } from '@/utils/currencyFormatter';
import { maskPixKey } from '@/utils/pixHelpers';
import EventBalanceCard from '@/components/EventBalanceCard.vue';

const props = defineProps({
  eventBalanceSummary: { type: Array, default: () => [] },
  requestingWithdrawalForEvent: { type: String, default: null },
  requestingEarlyPixForEvent: { type: String, default: null },
  fixedFee: { type: Number, default: 5 },
  loading: { type: Boolean, default: false },
  error: { type: String, default: '' },
  account: { type: Object, default: null },
  pixAccount: { type: Object, default: null },
  totalEmTransferencia: { type: Number, default: 0 },
  totalBloqueado: { type: Number, default: 0 },
});

const emit = defineEmits(['request-withdrawal', 'early-pix-withdrawal', 'retry']);

function onRequestWithdrawal(eventId, requestType) {
  emit('request-withdrawal', eventId, requestType);
}

function onEarlyPixWithdrawal(eventId) {
  emit('early-pix-withdrawal', eventId);
}

const pixExpanded = ref(false);
const bankExpanded = ref(false);
const payoutConfigExpanded = ref(false);

const maskedPixAccountKey = computed(() => {
  const keyType = props.pixAccount?.agency || props.pixAccount?.key_type;
  const masked = maskPixKey(props.pixAccount?.account || props.pixAccount?.pix_key, keyType);
  return masked || '—';
});

function pixKeyTypeLabel(keyType) {
  const labels = {
    CPF: 'CPF', CNPJ: 'CNPJ', EMAIL: 'E-mail',
    PHONE: 'Celular', EVP: 'Chave aleatória',
  };
  return labels[String(keyType || '').toUpperCase()] || keyType || 'Chave Pix';
}
</script>

<style scoped>
/* ── Skeleton loading ── */
.skeleton-card {
  background: #fff; border-radius: 16px; padding: 1.1rem 1.15rem;
  box-shadow: 0 2px 12px rgba(0,0,0,0.07);
}
.skeleton-card__header { display: flex; justify-content: space-between; margin-bottom: 0.75rem; }
.skeleton-card__body { display: flex; flex-direction: column; gap: 0.5rem; margin-bottom: 0.75rem; }
.skeleton-card__footer { display: flex; justify-content: flex-end; }
.skeleton-line {
  height: 14px; border-radius: 6px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-pulse 1.5s ease-in-out infinite;
}
.skeleton-line--sm { height: 12px; }
.skeleton-line--xs { height: 10px; }
.skeleton-line--lg { height: 22px; }
.skeleton-btn {
  width: 120px; height: 34px; border-radius: 8px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-pulse 1.5s ease-in-out infinite;
}
@keyframes skeleton-pulse {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* ── Section error state ── */
.section-error {
  text-align: center; padding: 2rem 1.5rem; background: #fff;
  border-radius: 16px; border: 1px solid #fecaca;
}
.section-error__icon { font-size: 1.5rem; color: #ef4444; margin-bottom: 0.5rem; }
.section-error__text { font-size: 0.85rem; color: #6b7280; margin-bottom: 0.75rem; }

/* ── Empty state: Saldos ── */
.sacar-empty-state {
  text-align: center; padding: 2.5rem 1.5rem; background: #fff;
  border-radius: 16px; border: 1px dashed #e2e8f0; margin-bottom: 1.5rem;
}
.sacar-empty-state__icon {
  width: 52px; height: 52px; border-radius: 50%;
  background: #fdf2f8; color: #e879a0;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 1.3rem; margin-bottom: 0.75rem;
}
.sacar-empty-state__title { font-size: 0.95rem; font-weight: 600; color: #1e293b; margin: 0 0 0.35rem; }
.sacar-empty-state__desc {
  font-size: 0.82rem; color: #94a3b8; margin: 0;
  max-width: 320px; margin-inline: auto; line-height: 1.5;
}

/* ── Configurações de Recebimento (colapsável) ── */
.payout-config { border-top: 1px solid #f3f4f6; padding-top: 0.75rem; }
.payout-config__toggle {
  display: flex; align-items: center; justify-content: space-between;
  width: 100%; background: none; border: none; padding: 0; cursor: pointer; gap: 0.5rem;
}
.payout-config__toggle-left { display: flex; align-items: center; }
.payout-config__toggle-label { font-size: 0.78rem; font-weight: 500; color: #9ca3af; letter-spacing: 0.01em; }
.payout-config__toggle:hover .payout-config__toggle-label { color: #6b7280; }

/* ── Grid de configuração (Dados Bancários + Pix) ── */
.setup-cards-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; margin-top: 0.25rem; }
@media (max-width: 639px) { .setup-cards-grid { grid-template-columns: 1fr; } }
.setup-card {
  background: #fff; border-radius: 16px; box-shadow: 0 2px 12px rgba(0,0,0,0.07);
  padding: 1.1rem 1.15rem 1rem; display: flex; flex-direction: column; gap: 0.75rem;
}
.setup-card__header { display: flex; align-items: center; gap: 0.65rem; }
.setup-card__icon-wrap {
  width: 38px; height: 38px; border-radius: 10px;
  display: flex; align-items: center; justify-content: center; font-size: 1rem; flex-shrink: 0;
}
.setup-card__icon-wrap--bank { background: #e0f7f5; color: #32BCAD; }
.setup-card__icon-wrap--pix  { background: #f0f4ff; color: #4f6ef7; }
.setup-card__titles { flex: 1; display: flex; flex-direction: column; gap: 0.05rem; }
.setup-card__title { font-size: 0.9rem; font-weight: 700; color: #1f2a44; line-height: 1.2; }
.setup-card__subtitle { font-size: 0.75rem; color: #8c96b0; line-height: 1.2; }
.setup-card__edit {
  color: #bcc4d8; font-size: 0.9rem; padding: 4px; transition: color 0.15s; flex-shrink: 0;
}
.setup-card__edit:hover { color: #ff50a3; }
.setup-card__body { display: flex; flex-direction: column; gap: 0.4rem; }
.setup-card__summary-row {
  display: flex; align-items: center; justify-content: space-between; cursor: pointer; user-select: none;
}
.setup-card__badge {
  display: inline-flex; align-items: center; font-size: 0.72rem; font-weight: 600;
  padding: 0.2rem 0.55rem; border-radius: 20px;
}
.setup-card__badge--ok      { background: #e8f8f1; color: #1f9b6a; }
.setup-card__badge--pending { background: #fff8e1; color: #b07c00; }
.setup-card__badge--warn    { background: #fdecea; color: #c0392b; }
.setup-card__toggle {
  font-size: 0.75rem; color: #8c96b0; display: flex; align-items: center; gap: 0.2rem; cursor: pointer;
}
.setup-card__fields {
  display: flex; flex-direction: column; gap: 0;
  border-top: 1px solid #f4f4f8; padding-top: 0.5rem; margin-top: 0.25rem;
}
.setup-card__field-row {
  display: flex; justify-content: space-between; padding: 0.3rem 0;
  border-bottom: 1px solid #f4f4f8; font-size: 0.8rem;
}
.setup-card__field-row--last { border-bottom: none; }
.setup-card__field-label { color: #8c96b0; }
.setup-card__field-value { color: #1f2a44; font-weight: 500; text-align: right; max-width: 60%; word-break: break-all; }
.setup-card__empty {
  display: flex; flex-direction: column; align-items: center;
  gap: 0.5rem; padding: 0.5rem 0 0.25rem; text-align: center;
}
.setup-card__empty-text { font-size: 0.8rem; color: #aab2c4; margin: 0; }
.setup-card__cta {
  display: inline-block; font-size: 0.8rem; font-weight: 700; color: #fff;
  background: #ff50a3; border-radius: 20px; padding: 0.3rem 1rem;
  text-decoration: none; transition: background 0.15s;
}
.setup-card__cta:hover { background: #e8417f; }

.pb-6 { padding-bottom: 3rem; }
</style>
